import React from 'react'
import { useTranslation } from 'react-i18next'
import { PreorderDisk } from '../../../api/dedicatedPreorderTypes'
import { Select } from '../../../components'
import { icons } from '../../../icons'
import { diskTypeLabel, formatCapacity } from '../list/format'
import { AddDisk, Aside, DiskIndex, DiskRow, ErrorLine, Hint, IconSquare, SplitLabel } from './ui'

type Props = {
  disks: PreorderDisk[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  error?: string
}

export const diskLabel = (disk: PreorderDisk) => `${disk.name || disk.model} · ${formatCapacity(disk.size_gb, 'GB')} ${diskTypeLabel(disk.disk_type)}`.trim()

export default function DiskSelection({ disks, selectedIds, onChange, error }: Props) {
  const [t] = useTranslation()
  const choices = disks.map(disk => ({ value: disk.id, label: diskLabel(disk) }))
  const chosen = selectedIds.map(id => disks.find(disk => disk.id === id)).filter(Boolean) as PreorderDisk[]
  const total = chosen.reduce((sum, disk) => sum + disk.size_gb, 0)

  return <div role="group" aria-label={String(t('STORAGE'))} data-test-id="preorder-disks"
    data-validation-field="disk_ids" data-validation-error={error ? true : undefined}
    tabIndex={error ? -1 : undefined} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <SplitLabel>
      <span>{t('STORAGE')} <Aside>· {selectedIds.length}</Aside></span>
      {total > 0 && <Hint>{t('DEDICATED_TOTAL')} {formatCapacity(total, 'GB')}</Hint>}
    </SplitLabel>
    {selectedIds.map((id, index) => <DiskRow key={index}>
      <DiskIndex aria-hidden="true">{index + 1}</DiskIndex>
      <Select required title={`${t('STORAGE')} ${index + 1}`} hideTitle fieldName="disk_ids" data={choices}
        selectedValue={id} onChange={value => onChange(selectedIds.map((current, i) => i === index ? value : current))}
        customStyles={{ width: '100%' }} hasSearch disabled={!disks.length}
        errors={error && !disks.some(disk => disk.id === id) ? { disk_ids: error } : undefined}
        placeholder={!disks.length ? t('DEDICATED_NO_COMPONENT_OPTIONS') : undefined} />
      <IconSquare type="button" aria-label={`${t('DELETE')} ${t('STORAGE')} ${index + 1}`}
        onClick={() => onChange(selectedIds.filter((_, i) => i !== index))}>
        <img src={icons.DeleteIcon} alt="" />
      </IconSquare>
    </DiskRow>)}
    {!selectedIds.length && error && <ErrorLine role="alert">{error}</ErrorLine>}
    <AddDisk type="button" disabled={!disks.length} onClick={() => onChange([...selectedIds, ''])}>
      <img src={icons.PlusDefault} alt="" />
      {t('DEDICATED_ADD_DISK')}
    </AddDisk>
  </div>
}
